// v10/src/components/QuickOpenModal.js
//
// Parte 4 — Registro rápido (apertura).
// Modal de salida del vuelo cuando el piloto elige (o tiene asignado) el modo
// "Registro rápido" en lugar del checklist asistido. Toma los datos de SALIDA
// (horómetro, combustible, aceite, notas) y abre el vuelo vía
// POST /api/flights/quick-open. La vuelta (cierre) usa el flujo existente
// (PostFlightModal / hobbs/:id/complete) — el cierre es el que pide la
// finalidad del vuelo, igual que con el checklist.
//
// Parte 5 — Bloque A (override AOG): el 409 AIRCRAFT_AOG y el 503
// AOG_CHECK_UNAVAILABLE ahora se manejan internamente con AogOverrideModal
// (mismo patrón que CompletionScreen para Valentina). El "Autorizar de todos
// modos" reintenta /flights/quick-open con override_aog + override_reason.
//
// Parte 5 — Bloque B (observaciones → squawks): si el piloto marca "Reportar
// desperfecto", tras abrir el vuelo el modal cambia al step de bifurcación
// (Volar igual / Pedir autorización al instructor), reusando el mismo
// /api/flight-auth que Valentina (con hobbs_record_id en vez de
// inspection_id). "Volar igual" además dispara el PUT /override existente
// para registrar que el piloto asumió como Comandante.
//
// Reusa estilos/inputs de PostFlightModal para mantener consistencia.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gauge, Fuel, StickyNote, CheckCircle2, X, Loader, Droplet, Zap, AlertTriangle, Shield, Plane, AlertCircle } from 'lucide-react';
import api, { flightService, aircraftService } from '../services/apiService';
import AogOverrideModal from './admin/lifespan/AogOverrideModal';
import { WaitingAuthScreen } from './CompletionScreen';

const QuickOpenModal = ({ aircraft, darkMode, onOpened, onDismiss, onOpenFlightConflict }) => {
  // El selector pasa una versión simplificada de la aeronave (sin
  // single_tank / fuel_capacity_*). Refrescamos vía getById para respetar el
  // contrato de combustible (idéntico a recordFuel del checklist).
  const [details, setDetails] = useState(null);

  const [hobbsStart, setHobbsStart]     = useState('');
  const [fuelLeft, setFuelLeft]         = useState('');
  const [fuelRight, setFuelRight]       = useState('');
  const [oilStart, setOilStart]         = useState('');
  const [notes, setNotes]               = useState('');

  // Parte 5 — Reporte de desperfecto.
  const [reportDefect, setReportDefect]           = useState(false);
  const [defectDescription, setDefectDescription] = useState('');

  // Step del modal:
  //   'form'        — carga de datos
  //   'bifurcation' — volar igual / pedir autorización tras el alta del vuelo con desperfecto
  //   'waiting'     — pantalla de espera con contador (reusada de Valentina)
  //   'retained'    — el instructor retuvo el avión (mensaje de cierre)
  const [step, setStep] = useState('form');

  // Vuelo ya abierto (lo necesitamos para enlazar el flight-auth por hobbs_record_id).
  const [openedRecord, setOpenedRecord] = useState(null);

  // Autorización creada tras "Pedir autorización" — alimenta WaitingAuthScreen.
  const [pendingAuth, setPendingAuth] = useState(null);
  const [retainedAuth, setRetainedAuth] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [saved, setSaved]     = useState(false);

  // AOG override (interno al modal: el AogOverrideModal monta encima).
  const [aogBlock, setAogBlock] = useState(null);

  // Bifurcación — loading por acción para que el spinner aparezca SOLO en el
  // botón tapeado (Fix 1+2 Parte 5). Valores: 'fly' | 'request' | null.
  const [loadingAction, setLoadingAction] = useState(null);
  const [bifurcationError, setBifurcationError] = useState('');

  useEffect(() => {
    if (!aircraft?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await aircraftService.getById(aircraft.id);
        if (!cancelled && res.data?.success && res.data.data) setDetails(res.data.data);
      } catch (_) {
        // Si falla, seguimos con lo que tengamos del selector y dejamos que
        // el backend valide capacidades.
      }
    })();
    return () => { cancelled = true; };
  }, [aircraft?.id]);

  const merged = { ...(aircraft || {}), ...(details || {}) };
  const fuelCapLeft  = merged.fuel_capacity_left  != null ? parseFloat(merged.fuel_capacity_left)  : null;
  const fuelCapRight = merged.fuel_capacity_right != null ? parseFloat(merged.fuel_capacity_right) : null;
  const singleTank   = merged.single_tank === true;
  const fuelUnit     = merged.fuel_unit || 'L';
  // Si la aeronave no tiene fuel_capacity_left/right cargados, todavía
  // mostramos los inputs (opcional), pero sin máximos.
  const hasFuelTracking = fuelCapLeft != null || fuelCapRight != null;

  const acName = aircraft
    ? `${aircraft.manufacturer || ''} ${aircraft.model_name || aircraft.name || ''}${aircraft.registration ? ' (' + aircraft.registration + ')' : ''}`.trim()
    : '';

  // Construye el body de quick-open desde el estado del formulario. Se reusa
  // tanto para el submit inicial como para el reintento tras el override AOG.
  const buildQuickOpenBody = (overrideArgs) => {
    const body = {
      aircraft_id: aircraft.id,
      hobbs_start: parseFloat(hobbsStart),
    };
    if (fuelLeft !== '') {
      const v = parseFloat(fuelLeft);
      if (!isNaN(v)) body.fuel_start_left = v;
    }
    if (!singleTank && fuelRight !== '') {
      const v = parseFloat(fuelRight);
      if (!isNaN(v)) body.fuel_start_right = v;
    }
    if (oilStart !== '') {
      const v = parseFloat(oilStart);
      if (!isNaN(v)) body.oil_start_qts = v;
    }
    const notesTrim = notes.trim();
    if (notesTrim) body.notes = notesTrim;
    if (overrideArgs?.override_aog) {
      body.override_aog    = true;
      body.override_reason = overrideArgs.override_reason;
    }
    return body;
  };

  // Resultado uniforme: vuelo abierto → bifurcación si hay desperfecto, si no
  // cierra el modal vía onOpened.
  const proceedAfterOpen = (record) => {
    if (reportDefect && defectDescription.trim().length > 0) {
      setOpenedRecord(record);
      setStep('bifurcation');
      return;
    }
    setSaved(true);
    setTimeout(() => onOpened?.({ data: record }), 1200);
  };

  const handleSubmit = async (overrideArgs) => {
    setError('');

    const hobbsNum = parseFloat(hobbsStart);
    if (!hobbsStart || isNaN(hobbsNum) || hobbsNum < 0) {
      setError('El horómetro de salida es requerido');
      return;
    }

    let leftNum  = null;
    let rightNum = null;
    if (fuelLeft !== '') {
      leftNum = parseFloat(fuelLeft);
      if (isNaN(leftNum) || leftNum < 0) {
        setError('Valor inválido para el tanque izquierdo');
        return;
      }
      if (fuelCapLeft != null && leftNum > fuelCapLeft) {
        setError(`Excede la capacidad del tanque izquierdo (${fuelCapLeft} ${fuelUnit})`);
        return;
      }
    }
    if (!singleTank && fuelRight !== '') {
      rightNum = parseFloat(fuelRight);
      if (isNaN(rightNum) || rightNum < 0) {
        setError('Valor inválido para el tanque derecho');
        return;
      }
      if (fuelCapRight != null && rightNum > fuelCapRight) {
        setError(`Excede la capacidad del tanque derecho (${fuelCapRight} ${fuelUnit})`);
        return;
      }
    }

    let oilNum = null;
    if (oilStart !== '') {
      oilNum = parseFloat(oilStart);
      if (isNaN(oilNum) || oilNum <= 0 || oilNum >= 99) {
        setError('El aceite debe estar entre 0 y 99 qt');
        return;
      }
    }

    // Si reporta desperfecto, la descripción es obligatoria (será el texto
    // del squawk).
    if (reportDefect && defectDescription.trim().length === 0) {
      setError('Describí el desperfecto antes de continuar');
      return;
    }

    setLoading(true);
    try {
      const body = buildQuickOpenBody(overrideArgs);
      const res = await flightService.quickOpen(body);
      // res.data → { success, message, data: hobbs_records }
      const record = res?.data?.data || null;
      if (!record) {
        setError('Respuesta inesperada del servidor');
        return;
      }
      // El override se aplicó correctamente: cerramos el modal de AOG si seguía
      // abierto, y seguimos el flujo normal (bifurcación o cierre).
      setAogBlock(null);
      proceedAfterOpen(record);
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};

      if (status === 409 && data.code === 'AIRCRAFT_HAS_OPEN_FLIGHT') {
        onOpenFlightConflict?.(data.open_flight);
        return;
      }
      if (status === 409 && data.error_code === 'AIRCRAFT_AOG') {
        // Parte 5: backend ahora devuelve can_override:true → el modal pinta
        // el form de override y al confirmar reintentamos vía handleAogConfirm.
        setAogBlock({ ...data, aircraft: { registration: aircraft.registration }, unavailable: false });
        return;
      }
      if (status === 503 && data.error_code === 'AOG_CHECK_UNAVAILABLE') {
        setAogBlock({ ...data, aircraft: { registration: aircraft.registration }, unavailable: true });
        return;
      }
      if (status === 403) {
        setError(data.message || 'No tenés permiso para usar el registro rápido.');
      } else if (status === 400) {
        setError(data.message || 'Revisá los datos e intentá de nuevo.');
      } else {
        setError(data.message || 'Error al abrir el vuelo. Intentá de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Confirmación desde AogOverrideModal: reintenta quick-open con override.
  // Si el reintento falla, dejamos el modal de AOG abierto y mostramos el
  // mensaje en su propio flujo (throw → onConfirm captura).
  const handleAogConfirm = async (reason) => {
    await handleSubmit({ override_aog: true, override_reason: reason });
  };

  // Crea el squawk + flight_authorization usando POST /api/flight-auth con
  // hobbs_record_id (en lugar de inspection_id). Devuelve el auth creado.
  const createDefectAuth = async () => {
    const text = defectDescription.trim();
    const res = await api.post('/api/flight-auth', {
      hobbs_record_id: openedRecord.id,
      aircraft_id:     aircraft.id,
      observations: [{
        text,
        comment:        null,
        pilot_decision: 'needs_review',
      }],
    });
    return res?.data?.data || null;
  };

  // "Volar igual" — crea el squawk + registra que el piloto asumió como
  // Comandante (mismo PUT /override que usa Valentina cuando timeout). Si la
  // autorización quedó pending (con needs_review siempre queda pending), el
  // override la pasa a 'pilot_override'.
  const handleVolarIgual = async () => {
    if (loadingAction) return;
    setBifurcationError('');
    setLoadingAction('fly');
    try {
      const auth = await createDefectAuth();
      if (auth?.id) {
        try {
          await api.put(`/api/flight-auth/${auth.id}/override`);
        } catch (_) {
          // Best-effort: si el override falla, el squawk igual quedó creado
          // y el flujo del instructor sigue su curso (clasificación posterior).
        }
      }
      setSaved(true);
      setTimeout(() => onOpened?.({ data: openedRecord }), 900);
    } catch (err) {
      setBifurcationError(err?.response?.data?.message || 'No se pudo registrar el desperfecto. Reintentá.');
    } finally {
      setLoadingAction(null);
    }
  };

  // "Pedir autorización" — crea el squawk; la cadena existente de
  // flightAuthController notifica al staff por push + bandeja + campanita
  // (vía 'observation_reported' que disparamos en el backend para el flujo
  // del registro rápido). Tras crear, pasamos a la pantalla de espera con
  // contador (mismo WaitingAuthScreen que Valentina) para igualar el
  // desenlace: autorizado / retenido / timeout → "Volar como Comandante".
  const handlePedirAutorizacion = async () => {
    if (loadingAction) return;
    setBifurcationError('');
    setLoadingAction('request');
    try {
      const auth = await createDefectAuth();
      if (!auth?.id) {
        setBifurcationError('Respuesta inesperada del servidor');
        return;
      }
      setPendingAuth(auth);
      setStep('waiting');
    } catch (err) {
      setBifurcationError(err?.response?.data?.message || 'No se pudo notificar al instructor. Reintentá.');
    } finally {
      setLoadingAction(null);
    }
  };

  // ── Desenlace de la pantalla de espera ────────────────────────────
  // El vuelo del registro rápido YA está abierto: las 3 ramas terminan
  // cerrando el modal. La diferencia con Valentina es que ahí el vuelo recién
  // arranca después de la autorización; acá ya arrancó.
  const handleAuthAuthorized = () => {
    setSaved(true);
    setTimeout(() => onOpened?.({ data: openedRecord }), 700);
  };
  const handleAuthRetained = (auth) => {
    setRetainedAuth(auth);
    setStep('retained');
  };
  const handleAuthOverride = () => {
    setSaved(true);
    setTimeout(() => onOpened?.({ data: openedRecord }), 700);
  };

  const bg   = darkMode ? '#111827' : 'white';
  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const soft = darkMode ? 'rgba(255,255,255,0.7)' : '#475569';
  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 15,
    fontWeight: 600, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    color: text,
  };

  // Pantalla de espera con contador — reusada de Valentina.
  // Misma UX, mismo timer, mismo desenlace que el checklist (Fix 5 Parte 5).
  if (step === 'waiting' && pendingAuth) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 110 }}>
        <WaitingAuthScreen
          authId={pendingAuth.id}
          authData={{
            status:          pendingAuth.status || 'pending',
            timeout_minutes: pendingAuth.timeout_minutes || 10,
          }}
          darkMode={darkMode}
          pilotName={''}
          onAuthorized={handleAuthAuthorized}
          onRetained={handleAuthRetained}
          onOverride={handleAuthOverride}
        />
      </div>
    );
  }

  // Avión retenido por el instructor — copy de cierre. El vuelo del registro
  // rápido YA está abierto, así que el "cierre" es solo informativo: el squawk
  // queda como AOG (lo hace decideFlightAuth) y futuros quick-open quedarán
  // bloqueados hasta que el squawk se resuelva.
  if (step === 'retained') {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 110, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', padding: 16,
                    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          style={{ maxWidth: 420, width: '100%',
                   background: darkMode ? 'rgba(20,12,12,0.96)' : 'white',
                   borderRadius: 28, padding: 36,
                   boxShadow: '0 24px 60px rgba(239,68,68,0.15)', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(239,68,68,0.1)',
                        border: '2px solid rgba(239,68,68,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px' }}>
            <AlertCircle style={{ width: 32, height: 32, color: '#ef4444' }} />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 900, color: '#dc2626' }}>
            Avión retenido
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: 14,
                      color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', lineHeight: 1.6 }}>
            El instructor decidió retener el avión. El squawk quedó marcado como AOG
            — no podrás volver a abrir un vuelo en esta aeronave hasta que se resuelva.
          </p>
          {retainedAuth?.instructor_note && (
            <div style={{ padding: '12px 16px', borderRadius: 14,
                          background: 'rgba(239,68,68,0.06)',
                          border: '1px solid rgba(239,68,68,0.15)',
                          marginBottom: 20, textAlign: 'left' }}>
              <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#ef4444',
                          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Nota del instructor
              </p>
              <p style={{ margin: 0, fontSize: 14,
                          color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' }}>
                {retainedAuth.instructor_note}
              </p>
            </div>
          )}
          <motion.button onClick={() => onOpened?.({ data: openedRecord })}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
            style={{ width: '100%', padding: 14, borderRadius: 16,
                     border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                     background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                     fontWeight: 600, fontSize: 14,
                     color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
            Entendido
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(0,0,0,0.6)',
                 backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center',
                 justifyContent: 'center', padding: 16 }}>
        <motion.div
          initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
          style={{ width: '100%', maxWidth: 420, borderRadius: 24, background: bg,
                   boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                   border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                   maxHeight: '90vh', overflowY: 'auto' }}>

          <div style={{ padding: '20px 24px 16px',
                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 14,
                            background: step === 'bifurcation'
                              ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                              : 'linear-gradient(135deg,#f59e0b,#f97316)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {step === 'bifurcation'
                  ? <AlertTriangle style={{ width: 20, height: 20, color: 'white' }} />
                  : <Zap style={{ width: 20, height: 20, color: 'white' }} />}
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
                  {step === 'bifurcation' ? 'Desperfecto reportado' : 'Registro rápido — Salida'}
                </h2>
                <p style={{ margin: 0, fontSize: 12, color: sub }}>{acName}</p>
              </div>
            </div>
            {step === 'form' && (
              <button onClick={onDismiss} disabled={loading}
                style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none',
                         borderRadius: 10, width: 34, height: 34, cursor: loading ? 'not-allowed' : 'pointer',
                         display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>

          {step === 'form' && (
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Gauge style={{ width: 13, height: 13 }} /> Horómetro al salir *
                </label>
                <input type="number" step="0.1" min="0" placeholder="Ej: 1234.5"
                  value={hobbsStart} onChange={e => setHobbsStart(e.target.value)}
                  style={inputStyle} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Fuel style={{ width: 13, height: 13 }} />
                  {hasFuelTracking ? 'Combustible de salida — opcional' : 'Combustible de salida (litros) — opcional'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <input type="number" step="0.1" min="0"
                      placeholder={singleTank
                        ? (fuelCapLeft != null ? `máx ${fuelCapLeft} ${fuelUnit}` : fuelUnit)
                        : (fuelCapLeft != null ? `Izq · máx ${fuelCapLeft}` : `Izq (${fuelUnit})`)}
                      value={fuelLeft}
                      onChange={e => { setFuelLeft(e.target.value); setError(''); }}
                      style={inputStyle} />
                  </div>
                  {!singleTank && (
                    <div style={{ flex: 1 }}>
                      <input type="number" step="0.1" min="0"
                        placeholder={fuelCapRight != null ? `Der · máx ${fuelCapRight}` : `Der (${fuelUnit})`}
                        value={fuelRight}
                        onChange={e => { setFuelRight(e.target.value); setError(''); }}
                        style={inputStyle} />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Droplet style={{ width: 13, height: 13 }} /> Aceite de salida (qt) — opcional
                </label>
                <input type="number" step="0.1" min="0" max="99" placeholder="Ej: 6.0"
                  value={oilStart} onChange={e => setOilStart(e.target.value)}
                  style={inputStyle} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <StickyNote style={{ width: 13, height: 13 }} /> Observaciones — opcional
                </label>
                <textarea rows={2} placeholder="Novedad de la salida, clima, etc."
                  value={notes} onChange={e => setNotes(e.target.value)}
                  style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} />
              </div>

              {/* Parte 5 — Reporte de desperfecto */}
              <label
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px', borderRadius: 12,
                  background: reportDefect
                    ? 'rgba(239,68,68,0.06)'
                    : (darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                  border: reportDefect
                    ? '1px solid rgba(239,68,68,0.25)'
                    : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0'),
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={reportDefect}
                  onChange={(e) => { setReportDefect(e.target.checked); setError(''); }}
                  disabled={loading}
                  style={{ marginTop: 2, width: 16, height: 16, accentColor: '#ef4444',
                           cursor: loading ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ fontSize: 13, color: soft, lineHeight: 1.4, fontWeight: 600 }}>
                  Reportar desperfecto
                  <div style={{ fontWeight: 400, fontSize: 11, color: sub, marginTop: 2 }}>
                    Crea un squawk para el instructor (igual que Valentina).
                  </div>
                </span>
              </label>

              {reportDefect && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: sub,
                                  display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                    <AlertTriangle style={{ width: 13, height: 13, color: '#ef4444' }} />
                    Descripción del desperfecto *
                  </label>
                  <textarea rows={3} placeholder="Ej: ruido extraño en motor, neumático bajo..."
                    value={defectDescription}
                    onChange={e => { setDefectDescription(e.target.value); setError(''); }}
                    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, minHeight: 70 }} />
                </div>
              )}

              {saved && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                           borderRadius: 12, background: 'rgba(22,163,74,0.1)',
                           border: '1px solid rgba(22,163,74,0.25)' }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                    ¡Vuelo abierto!
                  </span>
                </motion.div>
              )}
              {error && (
                <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600,
                            background: 'rgba(239,68,68,0.08)', padding: '8px 12px',
                            borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)' }}>
                  {error}
                </p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={onDismiss} disabled={loading}
                  style={{ flex: 1, padding: '12px', borderRadius: 14,
                           border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                           background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
                           fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: sub }}>
                  Cancelar
                </button>
                <button onClick={() => handleSubmit()} disabled={loading || saved}
                  style={{ flex: 2, padding: '12px', borderRadius: 14, border: 'none',
                           background: (loading || saved) ? '#64748b' : 'linear-gradient(135deg,#f59e0b,#f97316)',
                           cursor: (loading || saved) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                           fontSize: 13, fontWeight: 700, color: 'white',
                           display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  {loading
                    ? <><Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Abriendo…</>
                    : <><CheckCircle2 style={{ width: 14, height: 14 }} /> Abrir vuelo</>}
                </button>
              </div>
            </div>
          )}

          {step === 'bifurcation' && (
            <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{
                padding: '12px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.2)',
              }}>
                <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700,
                            color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Desperfecto a reportar
                </p>
                <p style={{ margin: 0, fontSize: 13, color: soft, lineHeight: 1.5 }}>
                  {defectDescription.trim()}
                </p>
              </div>

              <p style={{ margin: 0, fontSize: 14, color: soft, lineHeight: 1.5 }}>
                Tenés una observación, ¿qué hacés?
              </p>

              {bifurcationError && (
                <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600,
                            background: 'rgba(239,68,68,0.08)', padding: '8px 12px',
                            borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)' }}>
                  {bifurcationError}
                </p>
              )}

              {saved && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                           borderRadius: 12, background: 'rgba(22,163,74,0.1)',
                           border: '1px solid rgba(22,163,74,0.25)' }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                    Listo. Squawk registrado.
                  </span>
                </motion.div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Ambos botones se deshabilitan mientras uno procesa; el
                    spinner aparece SOLO en el botón tapeado (loadingAction). */}
                <motion.button
                  onClick={handleVolarIgual} disabled={!!loadingAction || saved}
                  whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: 14, border: 'none',
                           background: (loadingAction === 'fly' || saved)
                             ? '#64748b'
                             : loadingAction
                               ? 'rgba(100,116,139,0.5)'
                               : 'linear-gradient(135deg,#2563eb,#7c3aed)',
                           cursor: (loadingAction || saved) ? 'not-allowed' : 'pointer',
                           fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'white',
                           display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {loadingAction === 'fly'
                    ? <><Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> Procesando…</>
                    : <><Plane style={{ width: 16, height: 16 }} /> Volar igual (como Comandante)</>}
                </motion.button>

                <motion.button
                  onClick={handlePedirAutorizacion} disabled={!!loadingAction || saved}
                  whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: 14, border: 'none',
                           background: (loadingAction === 'request' || saved)
                             ? '#64748b'
                             : loadingAction
                               ? 'rgba(100,116,139,0.5)'
                               : 'linear-gradient(135deg,#f59e0b,#ef4444)',
                           cursor: (loadingAction || saved) ? 'not-allowed' : 'pointer',
                           fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'white',
                           display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {loadingAction === 'request'
                    ? <><Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> Procesando…</>
                    : <><Shield style={{ width: 16, height: 16 }} /> Pedir autorización al instructor</>}
                </motion.button>
              </div>

              <p style={{ margin: '4px 0 0', textAlign: 'center', fontSize: 11,
                          color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                El instructor clasifica el squawk como MEL (sale igual) o AOG (avión bloqueado).
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Parte 5 — AOG override interno (mismo modal que usa Valentina). */}
      {aogBlock && (
        <AogOverrideModal
          isOpen={true}
          aogData={aogBlock}
          unavailable={!!aogBlock.unavailable}
          onConfirm={handleAogConfirm}
          onCancel={() => setAogBlock(null)}
          darkMode={darkMode}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AnimatePresence>
  );
};

export default QuickOpenModal;
